/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _5 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>5 - Область видимости</h2>
    <p className={styles.description}>
      <b>Область видимости (scope)</b> — это независимая от языка концепция, которая описывает доступность переменных в
      исполняемом коде.
    </p>
    <p className={styles.description}>
      <b>Scope chain (цепочка областей видимости)</b> - области видимости образуют иерархию, так что дочерние области
      имеют доступ к переменным из родительских областей, но не наоборот.
    </p>
    <p className={styles.notificationInfo}>
      Переменная видна для исполняемого кода, если она есть в текущей области видимости или в цепочке областей видимости
      функции.
    </p>
    <p className={styles.description}>Есть три типа областей видимости: </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        Переменные, объявленные на самом верхнем уровне, то есть вне любых конструкций вроде{' '}
        <code className={styles.code}>if</code>, <code className={styles.code}>while</code>,{' '}
        <code className={styles.code}>for</code> и функций, находятся в<b>глобальной</b> области видимости.
      </li>
      <li className={styles.list__item}>
        Переменные, объявленные внутри инструкций <code className={styles.code}>if</code>, циклов и других блоков кода
        находятся в <b>блочной</b> области видимости.
      </li>
      <li className={styles.list__item}>
        Переменные, объявленные внутри функций, находятся в <b>области видимости функции</b>.
      </li>
    </ul>
    <p className={styles.description}>
      Это можно представить как дом с комнатами. Дом находится в глобальной области видимости. Каждая функция и блок
      создают новую комнату, вложенную внутрь дома. Переменные, объявленные внутри этих комнат, доступны только тогда,
      когда вы находитесь внутри этой комнаты. Вне комнаты эти переменные недоступны.
    </p>
    <CopyBlock
      text={`// Global scope

function foo() {
  // Local function scope of foo

  if (true) {
    // Local block scope
  }

  // Local function scope of foo

  function bar() {
    // Local function scope of bar
  }

  // Local function scope of foo
}

// Global scope`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Интерпретатор пытается сначала найти переменную в той области видимости, в которой к ней обратились. Если такой
      переменной в локальной области видимости нет, то он выходит наружу, на один уровень за попытку, пока не найдёт
      значение или не дойдет до самой верхней области видимости (глобальной) и поймет, что переменную с таким
      идентификатором невозможно найти, так как ее просто нет, тогда будет ошибка о том, что переменная не объявлена.{' '}
    </p>
    <p className={styles.description}>
      Функция <code className={styles.code}>add</code> возвращает сумму <code className={styles.code}>a</code> и{' '}
      <code className={styles.code}>b</code>. Переменная <code className={styles.code}>a</code> объявлена внутри
      функции, <code className={styles.code}>b</code> нет.{' '}
    </p>
    <CopyBlock
      text={`const b = 10;

const add = function() {
  const a = 5;

  return a + b;
};

add(); // 15`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      Пытаясь решить выражение <code className={styles.code}>a + b</code>, интерпретатор ищет значения{' '}
      <code className={styles.code}>a</code> и <code className={styles.code}>b</code>. Поиск начинается внутри локальной
      области видимости — внутри функции <code className={styles.code}>add</code>. Он находит значение{' '}
      <code className={styles.code}>a</code> и переходит к <code className={styles.code}>b</code>. Невозможно найти
      значение <code className={styles.code}>b</code> в локальной области видимости, поэтому поиск расширятеся до
      наружной области. Тут он находит <code className={styles.code}>b</code> — это{' '}
      <code className={styles.code}>10</code>. Выражение <code className={styles.code}>a + b</code> превращается в{' '}
      <code className={styles.code}>5 + 10</code>, в результате получаем <code className={styles.code}>15</code>.
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>5.1 - Область видимости функции</h3>
      <p className={styles.description}>
        Как уже говорилось, функции создают собственную локальную область видимости. Переменные, созданные внутри
        функции, включая параметры, локальны внутри этой функции и не доступны коду из вне. Локальные переменные будут
        создаваться каждый раз при вызове функции, и их отдельные инкарнации никак друг с другом не связаны.{' '}
      </p>
      <p className={styles.description}>
        Глобальный <code className={styles.code}>value</code> объявлен вне тела функции и его значение будет выведено в
        консоль. Локальный <code className={styles.code}>value</code> внутри функции{' '}
        <code className={styles.code}>add</code> по прежнему виден только внутри этой функции. Эти два{' '}
        <code className={styles.code}>value</code> не имеют ничего общего друг с другом, они находятся в разных областях
        видимости. Они не схлопываются в одно целое, не смотря на то, что у них одно и то же имя. В свою очередь{' '}
        <code className={styles.code}>innerValue</code> не доступна вне тела функции.
      </p>{' '}
      <CopyBlock
        text={`const value = 50;

const add = function(num) {
  const value = 10;
  const innerValue = 5;

  return num + value + innerValue;
};

// value объявлен глобально и поэтому доступен
console.log(value); // 50

console.log(add(20)); // 35

// Ошибка, переменная innerValue не объявлена в этой области
// видимости, она доступна только внутри фукции add
console.log(innerValue);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Такое поведение помогает предотвратить случайное взаимодействие между функциями. Если бы все переменные
        использовались в любом месте программы, было бы очень трудно убедиться, что одна переменная не используется для
        разных операций.{' '}
      </p>
      <p className={styles.description}>
        Относясь к локальным для функций переменным так, что они существуют только внутри функции, язык делает возможным
        работу с функциями будто с изолированными контейнерами, что позволяет не волноваться про весь код целиком.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>5.2 - Hoisting</h3>
      <p className={styles.description}>
        В языках программирования, в том числе в JavaScript, код исполняется в две фазы.{' '}
      </p>
      <p className={styles.description}>
        <b>Фаза компиляции, интерпретации или оценки (compile time, evaluation time)</b> - подготовка перед исполнением
        кода, проверка валидности синтаксиса исходного кода.{' '}
      </p>
      <p className={styles.description}>
        Во время этой фазы компилятор или интерпретатор находит синтаксические ошибки, ошибки типизации и т. д. То есть
        код еще не выполняется, только оценивается. Если эта фаза прошла успешно, это как минимум значит что в коде нет
        синтаксических ошибок и его можно запустить на исполнение.{' '}
      </p>
      <p className={styles.description}>
        <b>Фаза исполнения (runtime)</b> - скрипт начинает исполняться, выполняются инструкции вызовов функций и оценки
        выражений, происходит поиск необходимых идентификаторов в соответствующих областях видимости и тому подобное.{' '}
      </p>
      <p className={styles.description}>
        Если эта фаза завершилась успешно, значит скрипт написан без явных ошибок и закончил свою работу. На этой фазе
        могут быть ошибки связанные с отсутствующими свойствами и переменными, преобразованием типов и т. д., то есть
        что-то, что происходит только во время выполнения кода.{' '}
      </p>
      <p className={styles.description}>
        Попробуйте выполнить следующий код. Так как мы сделали опечатку и вместо{' '}
        <code className={styles.code}>const</code> пытаемся объявить переменную{' '}
        <code className={styles.code}>value</code> ключевым словом <code className={styles.code}>cos</code>, на фазе
        компиляции будет выявлена синтаксическая ошибка и фаза исполнения даже не запустится. В консоли мы сразу увидим
        сообщение об ошибке.{' '}
      </p>
      <CopyBlock
        text={`console.log('Этого сообщения не будет в консоли.');

cos value = 5;`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <b>Поднятие переменных (hoisting)</b> - это механизм интерпретатора, который, до фазы исполнения кода, поднимает
        объявления переменных в начало области видимости (блочной или функции) в которой они были объявлены.{' '}
      </p>
      <p className={styles.description}>
        Именно поэтому работает <code className={styles.code}>function declaration</code> и так странно ведут себя
        переменные объявленные используя <code className={styles.code}>var</code> - их объявления поднимаются в начало
        области видимости функции в которой они были объявлены.{' '}
      </p>
      <CopyBlock
        text={`// Вот поэтому мы используем let или const

console.log(value); // undefined
value = 5;

if (true) {
  console.log(value); // 5
  var value = 10;
  console.log(value); // 10
}

value = 15;
console.log(value); // 15`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Переменные объявленные используя <code className={styles.code}>let</code> или{' '}
        <code className={styles.code}>const</code> так же поднимаются, но при этом подчиняются блочной области
        видимости, ничем не инициализируются по умолчанию и не доступны для обращения до того места в коде где были
        объявлены в коде.
      </p>
      <CopyBlock
        text={`// В каждой области видимости будет создана своя, независимая переменная value

console.log(value); // ReferenceError: value is not defined
const value = 5;
console.log(value); // 5

if (true) {
  console.log(value); // ReferenceError: value is not defined
  const value = 10;
  console.log(value); // 10
}

console.log(value); // 5`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Более детально про поднятие идентификаторов и ключевые словах <code className={styles.code}>var</code>,{' '}
        <code className={styles.code}>let</code> и<code className={styles.code}>const</code> читайте{' '}
        <a
          className={styles.link}
          href="https://codeburst.io/const-let-and-var-which-and-when-541a2721c18"
          target="_blank"
          rel="noopener noreferrer"
        >
          в этой статье
        </a>
        .
      </p>
    </section>
  </section>
))
